import { vaultHotswap } from './vaultHotswap';
// src/runtime/shared/vaultConfig.ts
// ============================================
// Obsidian Vault 统一路径配置
//
// 核心原则：
//   1. 真实 Vault（D:/Obsidian/小舍AI_中央大脑库）是唯一真相源
//   2. XEAOS 后端所有 Runtime 通过此模块获取 Vault 路径
//   3. 严禁任何 Runtime 硬编码 'obsidian-vault' 字符串
//   4. 浏览器环境（无 fs）自动降级为 MCP 调用
//
// 相关文档：
//   - 真实 Vault 结构：D:/Obsidian/小舍AI_中央大脑库/
//   - 知识标准：09_Knowledge（知识中心）/02_Knowledge_Standards.md
//   - MCP Provider：src/runtime/KnowledgeRuntime/providers/ObsidianMCPProvider.ts
// ============================================

/**
 * Vault 路径配置
 */
export interface VaultConfig {
  /** Vault 根目录绝对路径 */
  vaultRoot: string;
  /** XEAOS 业务子目录名（在 Vault 中的落点） */
  xeaosSubdir: string;
  /** XEAOS 业务完整路径 = vaultRoot + xeaosSubdir */
  xeaosPath: string;
  /** 是否为浏览器环境（无 fs） */
  isBrowser: boolean;
  /** MCP URL（Obsidian Local REST API） */
  mcpUrl: string;
  /** MCP API Key */
  mcpApiKey: string;
  /** 是否启用 MCP（优先 MCP，降级 fs） */
  preferMcp: boolean;
}

/**
 * 获取环境变量（兼容 Node 与浏览器）
 */
function getEnv(key: string, fallback: string = ''): string {
  if (typeof import.meta !== 'undefined' && import.meta.env) {
    return (import.meta.env as Record<string, string>)[key] || fallback;
  }
  if (typeof process !== 'undefined' && process.env) {
    return process.env[key] || fallback;
  }
  return fallback;
}

/**
 * 内部可变状态初始值（启动时确定）
 *
 * 优先级：
 *   1. 环境变量 VITE_OBSIDIAN_VAULT_ROOT
 *   2. 默认值 'D:/Obsidian/小舍AI_中央大脑库'（真实 Vault 路径）
 *
 * 注意：默认值指向真实 Vault，不再使用旧的 'obsidian-vault' 相对路径
 */
const _initialConfig = (() => {
  const isBrowser =
    typeof window !== 'undefined' ||
    (typeof import.meta !== 'undefined' && import.meta.env?.DEV !== undefined && typeof process === 'undefined');

  const vaultRoot = getEnv(
    'VITE_OBSIDIAN_VAULT_ROOT',
    'D:/Obsidian/小舍AI_中央大脑库'
  );
  const xeaosSubdir = getEnv('VITE_OBSIDIAN_XEAOS_SUBDIR', 'XEAOS');
  // 企业大脑热插拔：Obsidian 连接参数优先读取 vaultHotswap（设置中心可配置）
  let mcpUrl = getEnv('VITE_OBSIDIAN_URL', 'http://127.0.0.1:27123/mcp/');
  // P12 bundle 密钥治理：Obsidian REST MCP Key 是本地 127.0.0.1 的 token，
  // 不再从 VITE_OBSIDIAN_API_KEY 读取（避免被 Vite 内联进前端 bundle），
  // 仅通过 vaultHotswap（设置中心手动配置，存 localStorage）注入。
  let mcpApiKey = '';
  try {
    const hs = vaultHotswap.get();
    if (hs.obsidianUrl) mcpUrl = hs.obsidianUrl;
    if (hs.obsidianApiKey) mcpApiKey = hs.obsidianApiKey;
  } catch {
    // vaultHotswap 不可用时使用环境变量（mcpApiKey 已改为仅空串兜底）
  }

  return {
    vaultRoot,
    xeaosSubdir,
    xeaosPath: `${vaultRoot}/${xeaosSubdir}`,
    isBrowser,
    mcpUrl,
    mcpApiKey,
    preferMcp: Boolean(mcpApiKey) && !isBrowser,
  };
})();

// 可变的 vault 配置状态（热插拔时通过 vaultConfig.update() 修改）
let _vaultConfig = { ..._initialConfig };

// 订阅者列表（配置变更时通知）
const _subscribers = new Set<() => void>();

/**
 * Vault 配置（单例，支持热插拔）
 *
 * 读取通过 getter（始终返回最新值），修改通过 update()（通知所有订阅者）。
 * 设置面板切换 Vault 路径后调用 update() 即可触发刷新。
 *
 * 注意：为保证向后兼容，KnowledgePaths / XeaosPaths 等导出常量仍以初始值
 * 在模块加载时计算一次；需要响应热插拔的新代码请改用 getXeaosPaths() 等
 * 动态 getter 函数。
 */
export const vaultConfig: VaultConfig & {
  /** 更新配置（热插拔），传入部分字段即可；会自动重算派生字段并通知订阅者 */
  update(updates: Partial<{
    vaultRoot: string;
    xeaosSubdir: string;
    mcpUrl: string;
    mcpApiKey: string;
    preferMcp: boolean;
  }>): void;
  /** 订阅配置变更，返回取消订阅函数 */
  subscribe(fn: () => void): () => void;
} = {
  get vaultRoot() { return _vaultConfig.vaultRoot; },
  get xeaosSubdir() { return _vaultConfig.xeaosSubdir; },
  get xeaosPath() { return _vaultConfig.xeaosPath; },
  get isBrowser() { return _vaultConfig.isBrowser; },
  get mcpUrl() { return _vaultConfig.mcpUrl; },
  get mcpApiKey() { return _vaultConfig.mcpApiKey; },
  get preferMcp() { return _vaultConfig.preferMcp; },

  update(updates) {
    _vaultConfig = { ..._vaultConfig, ...updates };
    // vaultRoot 或 xeaosSubdir 变更时，重算 xeaosPath
    if (updates.vaultRoot !== undefined || updates.xeaosSubdir !== undefined) {
      _vaultConfig.xeaosPath = `${_vaultConfig.vaultRoot}/${_vaultConfig.xeaosSubdir}`;
    }
    // mcpApiKey 变更时，重算 preferMcp（保持原有逻辑：有 key 且非浏览器环境才优先 MCP）
    if (updates.mcpApiKey !== undefined) {
      _vaultConfig.preferMcp = Boolean(_vaultConfig.mcpApiKey) && !_vaultConfig.isBrowser;
    }
    _subscribers.forEach((fn) => {
      try { fn(); } catch (e) { /* 订阅者异常不影响其他订阅者 */ }
    });
  },

  subscribe(fn) {
    _subscribers.add(fn);
    return () => { _subscribers.delete(fn); };
  },
};

/**
 * 真实 Vault 知识中心路径
 *
 * 对应真实 Vault 目录：09_Knowledge（知识中心）/
 * 包含：Domain / Ideas / Business / _System / Cards / Reviews / Outputs / Resources / Skills
 */
export const KNOWLEDGE_CENTER_PATH = `${vaultConfig.vaultRoot}/09_Knowledge（知识中心）`;

/**
 * 知识中心子目录路径常量
 */
export const KnowledgePaths = {
  /** 领域知识根目录：09_Knowledge（知识中心）/Domain/ */
  domain: `${KNOWLEDGE_CENTER_PATH}/Domain`,
  /** Agent 域：09_Knowledge（知识中心）/Domain/Agent/ */
  domainAgent: `${KNOWLEDGE_CENTER_PATH}/Domain/Agent`,
  /** LLM 域 */
  domainLLM: `${KNOWLEDGE_CENTER_PATH}/Domain/LLM`,
  /** Quant 域 */
  domainQuant: `${KNOWLEDGE_CENTER_PATH}/Domain/Quant`,
  /** Sales 域 */
  domainSales: `${KNOWLEDGE_CENTER_PATH}/Domain/Sales`,
  /** CRM 域 */
  domainCRM: `${KNOWLEDGE_CENTER_PATH}/Domain/CRM`,
  /** Marketing 域 */
  domainMarketing: `${KNOWLEDGE_CENTER_PATH}/Domain/Marketing`,
  /** Workflow 域 */
  domainWorkflow: `${KNOWLEDGE_CENTER_PATH}/Domain/Workflow`,

  /** Ideas 目录（13 个赛道知识库） */
  ideas: `${KNOWLEDGE_CENTER_PATH}/Ideas`,
  /** 业务知识根目录 */
  business: `${KNOWLEDGE_CENTER_PATH}/Business`,
  /** 系统知识根目录（SOP/Workflow/Prompt/Templates） */
  system: `${KNOWLEDGE_CENTER_PATH}/_System`,
  /** SOP 目录 */
  sop: `${KNOWLEDGE_CENTER_PATH}/_System/SOP`,
  /** Workflow 目录 */
  workflow: `${KNOWLEDGE_CENTER_PATH}/_System/Workflow`,
  /** Prompt 目录 */
  prompt: `${KNOWLEDGE_CENTER_PATH}/_System/Prompt`,
  /** Templates 目录 */
  templates: `${KNOWLEDGE_CENTER_PATH}/_System/Templates`,
  /** Best Practices 目录 */
  bestPractices: `${KNOWLEDGE_CENTER_PATH}/_System/Best_Practices`,

  /** Cards 目录 */
  cards: `${KNOWLEDGE_CENTER_PATH}/Cards`,
  /** Reviews 目录 */
  reviews: `${KNOWLEDGE_CENTER_PATH}/Reviews`,
  /** Outputs 目录 */
  outputs: `${KNOWLEDGE_CENTER_PATH}/Outputs`,
  /** Resources 目录 */
  resources: `${KNOWLEDGE_CENTER_PATH}/Resources`,
  /** Skills 目录 */
  skills: `${KNOWLEDGE_CENTER_PATH}/Skills`,
} as const;

/**
 * XEAOS 业务路径常量（Vault 中的 XEAOS/ 子目录）
 *
 * 注意：真实 Vault 已有 03~08 Projects 等项目目录，
 * XEAOS 自身的 CRM/SKU/SOP 等业务数据放在 Vault 根下的 XEAOS/ 子目录。
 *
 * 子目录命名与 VaultSection 枚举保持一致（参见 EnterpriseRuntime/workspace/types.ts）
 */
export const XeaosPaths = {
  /** XEAOS 业务根目录 */
  root: vaultConfig.xeaosPath,
  /** CRM 客户档案目录 */
  crm: `${vaultConfig.xeaosPath}/CRM`,
  /** SKU 产品目录 */
  sku: `${vaultConfig.xeaosPath}/SKU`,
  /** SOP 流程目录（与 Knowledge/_System/SOP 区分：此为业务侧 SOP） */
  sop: `${vaultConfig.xeaosPath}/SOP`,
  /** 会议纪要目录（单数，与 VaultSection.Meeting 一致） */
  meeting: `${vaultConfig.xeaosPath}/Meeting`,
  /** 项目档案目录 */
  project: `${vaultConfig.xeaosPath}/Project`,
  /** 调研报告目录 */
  research: `${vaultConfig.xeaosPath}/Research`,
  /** 日报/周报目录 */
  daily: `${vaultConfig.xeaosPath}/Daily`,
  /** 工作流案例目录 */
  workflow: `${vaultConfig.xeaosPath}/Workflow`,
  /** Prompt 模板目录 */
  prompt: `${vaultConfig.xeaosPath}/Prompt`,
  /** AI 记忆目录 */
  memory: `${vaultConfig.xeaosPath}/Memory`,
  /** 财务记录目录 */
  finance: `${vaultConfig.xeaosPath}/Finance`,
  /** 团队成员目录 */
  team: `${vaultConfig.xeaosPath}/Team`,
  /** 合同目录（ContractGenerator 专用，不在 VaultSection 12 大模块中） */
  contracts: `${vaultConfig.xeaosPath}/contracts`,
} as const;

/**
 * 兼容旧路径：返回真实 Vault 路径
 *
 * 此函数用于平滑迁移：旧代码用 'obsidian-vault' 相对路径，
 * 调用此函数即可获得真实 Vault 路径。
 *
 * @deprecated 请直接使用 vaultConfig.vaultRoot 或 KnowledgePaths 常量
 */
export function resolveVaultPath(legacyPath: string = ''): string {
  // 兼容：把 'obsidian-vault/...' 替换为真实 Vault 路径
  if (legacyPath.startsWith('obsidian-vault')) {
    const suffix = legacyPath.slice('obsidian-vault'.length);
    return `${vaultConfig.vaultRoot}${suffix}`;
  }
  return legacyPath;
}

/**
 * 解析 XEAOS 业务路径
 *
 * 旧代码 'obsidian-vault/XEAOS/CRM' → 真实路径 '{vaultRoot}/XEAOS/CRM'
 */
export function resolveXeaosPath(legacyPath: string = ''): string {
  if (legacyPath.startsWith('obsidian-vault/XEAOS')) {
    const suffix = legacyPath.slice('obsidian-vault/XEAOS'.length);
    return `${vaultConfig.xeaosPath}${suffix}`;
  }
  return legacyPath;
}

// ============================================
// 动态路径 getter（响应热插拔）
// ============================================
// 说明：上方导出的 KnowledgePaths / XeaosPaths / KNOWLEDGE_CENTER_PATH
// 为保持向后兼容，仍以初始值在模块加载时计算一次；热插拔后需要最新路径
// 的场景（如设置面板、新接入的 Runtime）请改用下列 getter 函数。

/**
 * 获取当前知识中心根路径（响应热插拔）
 */
export function getKnowledgeCenterPath(): string {
  return `${vaultConfig.vaultRoot}/09_Knowledge（知识中心）`;
}

/**
 * 获取当前知识中心子目录路径（响应热插拔）
 * 与 KnowledgePaths 常量一一对应，但每次调用都基于最新 vaultConfig 计算
 */
export function getKnowledgePaths() {
  const root = getKnowledgeCenterPath();
  return {
    domain: `${root}/Domain`,
    domainAgent: `${root}/Domain/Agent`,
    domainLLM: `${root}/Domain/LLM`,
    domainQuant: `${root}/Domain/Quant`,
    domainSales: `${root}/Domain/Sales`,
    domainCRM: `${root}/Domain/CRM`,
    domainMarketing: `${root}/Domain/Marketing`,
    domainWorkflow: `${root}/Domain/Workflow`,
    ideas: `${root}/Ideas`,
    business: `${root}/Business`,
    system: `${root}/_System`,
    sop: `${root}/_System/SOP`,
    workflow: `${root}/_System/Workflow`,
    prompt: `${root}/_System/Prompt`,
    templates: `${root}/_System/Templates`,
    bestPractices: `${root}/_System/Best_Practices`,
    cards: `${root}/Cards`,
    reviews: `${root}/Reviews`,
    outputs: `${root}/Outputs`,
    resources: `${root}/Resources`,
    skills: `${root}/Skills`,
  };
}

/**
 * 获取当前 XEAOS 业务路径（响应热插拔）
 * 与 XeaosPaths 常量一一对应，但每次调用都基于最新 vaultConfig 计算
 */
export function getXeaosPaths() {
  const root = `${vaultConfig.vaultRoot}/${vaultConfig.xeaosSubdir}`;
  return {
    root,
    crm: `${root}/CRM`,
    sku: `${root}/SKU`,
    sop: `${root}/SOP`,
    meeting: `${root}/Meeting`,
    project: `${root}/Project`,
    research: `${root}/Research`,
    daily: `${root}/Daily`,
    workflow: `${root}/Workflow`,
    prompt: `${root}/Prompt`,
    memory: `${root}/Memory`,
    finance: `${root}/Finance`,
    team: `${root}/Team`,
    contracts: `${root}/contracts`,
  };
}

export default vaultConfig;
